import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import { discountLabel } from "../lib/brand";
import { useAuth } from "../lib/auth";
import { MapPin, Clock, Phone, Globe } from "@phosphor-icons/react";
import VisitDialog from "../components/brand/ReservationDialog";

export default function MerchantDetail() {
  const { id } = useParams();
  const [merchant, setMerchant] = useState(null);
  const [showVisit, setShowVisit] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/merchants/${id}`).then(({ data }) => setMerchant(data));
  }, [id]);

  if (!merchant) return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader /><div className="max-w-7xl mx-auto px-6 py-20 text-white/50">Loading…</div>
    </div>
  );

  const cover = merchant.cover_url || merchant.gallery?.[0];
  const branch = merchant.branches?.find((b) => b.is_primary) || merchant.branches?.[0];

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      {/* Cover */}
      <section className="relative">
        <div className="aspect-[16/8] sm:aspect-[16/6] bg-[#0a0a0a] relative">
          {cover && <img src={cover} alt={merchant.business_name} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-6 -mt-24 sm:-mt-32 relative">
          <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-end">
            {merchant.logo_url ? (
              <img src={merchant.logo_url} alt={merchant.business_name} className="h-28 w-28 rounded-md object-cover ring-1 ring-[#D4AF37]/40 bg-black" />
            ) : (
              <div className="h-28 w-28 rounded-md bg-[#0d0d0d] border border-[#D4AF37]/40 flex items-center justify-center font-display text-3xl text-[#D4AF37]">
                {merchant.business_name.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              {merchant.category?.name && (
                <span className="text-[10px] tracking-luxe uppercase text-[#D4AF37] bg-black/70 border border-[#D4AF37]/30 px-3 py-1 rounded-full">{merchant.category.name}</span>
              )}
              <h1 className="mt-3 font-display text-4xl sm:text-6xl text-white leading-tight">{merchant.business_name}</h1>
              {branch && <p className="text-white/60 mt-2 text-sm flex items-center gap-2"><MapPin size={14} className="text-[#D4AF37]" />{branch.branch_name}{branch.city ? ` · ${branch.city}` : ""}</p>}
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <div className="bg-[#0d0d0d] border border-[#D4AF37]/40 rounded-md px-4 py-3 text-right">
                <p className="text-[10px] tracking-luxe uppercase text-white/50">Member Privilege</p>
                <p className="font-display text-2xl text-[#D4AF37]">{discountLabel(merchant.member_discount_type, merchant.member_discount_value)}</p>
                <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-1">Active members only</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          {merchant.description && (
            <div>
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">About</p>
              <p className="text-white/75 mt-3 leading-relaxed">{merchant.description}</p>
            </div>
          )}
          {merchant.promo_text && (
            <div className="border border-[#D4AF37]/30 bg-[#0d0d0d] p-6 rounded-md">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Tonight's Promo</p>
              <p className="font-display text-2xl text-white mt-2">{merchant.promo_text}</p>
            </div>
          )}
          {merchant.terms_and_conditions?.trim() && (
            <div data-testid="merchant-terms-section">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Terms & Conditions</p>
              <div className="tsc-card p-5 mt-3 max-h-72 overflow-y-auto">
                <p className="text-white/70 text-sm whitespace-pre-wrap leading-relaxed">{merchant.terms_and_conditions}</p>
              </div>
            </div>
          )}
          {merchant.gallery?.length > 0 && (
            <div>
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Gallery</p>
              <div className="grid sm:grid-cols-3 gap-3 mt-4">
                {merchant.gallery.map((g, i) => (
                  <img key={i} src={g} alt="" className="aspect-square object-cover rounded-md" />
                ))}
              </div>
            </div>
          )}
          {merchant.branches?.length > 0 && (
            <div>
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Branches</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                {merchant.branches.map((b) => (
                  <div key={b.id} className="tsc-card p-5">
                    <p className="font-display text-xl text-white">{b.branch_name}</p>
                    {b.address && <p className="text-white/60 text-sm mt-1 flex gap-2 items-start"><MapPin size={14} className="text-[#D4AF37] mt-1" />{b.address}</p>}
                    {b.phone && <p className="text-white/60 text-sm mt-1 flex gap-2 items-center"><Phone size={14} className="text-[#D4AF37]" />{b.phone}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="tsc-card p-6 sticky top-24">
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Member Privileges</p>
            <p className="font-display text-2xl text-white mt-2">Register your visit</p>
            <p className="text-white/60 text-sm mt-2">The Social Circle is a members-only network. Sign in to register your visit and present your member QR at the venue.</p>
            <button onClick={() => setShowVisit(true)} className="tsc-btn-gold w-full mt-6" data-testid="register-visit-btn">Register Visit</button>
            {!user && (
              <p className="text-white/45 text-xs mt-4">Not a member yet? <Link to="/register" className="text-[#D4AF37]">Apply for membership</Link></p>
            )}
            {user?.role === "member" && (
              <button onClick={() => nav("/me")} className="tsc-btn-outline w-full mt-3" data-testid="show-member-qr-btn">Show my Member QR</button>
            )}
          </div>
        </aside>
      </section>

      {showVisit && <VisitDialog merchant={merchant} onClose={() => setShowVisit(false)} />}
      <Footer />
    </div>
  );
}
